import { createMemo, For, onCleanup, onMount, Show } from "solid-js";
import type { Arete } from "../domaine/graphe";
import { formaterScore, pluriel } from "../format";
import type { Analyse } from "./analyse";
import { FicheProjet } from "./FicheProjet";
import { declarerFigure } from "./Figure";

/**
 * Volet de la fiche projet. Il n'existe que lorsqu'un projet est choisi : la vue
 * garde toute la largeur le reste du temps. Il se ferme par sa croix, Échap ou un
 * clic en dehors.
 */
export function Panneau(props: { analyse: Analyse }) {
  const a = () => props.analyse;
  const choix = createMemo(() => {
    const projets = a().portefeuille.projets;
    const indice = projets.findIndex((p) => p.ref === a().selection);
    const projet = projets[indice];
    return projet ? { projet, indice } : null;
  });

  let volet: HTMLElement | undefined;
  onMount(() => {
    const echap = (e: KeyboardEvent) => {
      if (e.key === "Escape") a().selectionner(null);
    };
    // Un clic en dehors ferme le volet, sauf s'il choisit un autre projet (la fiche
    // suit) ou s'il termine un glissement (déplacer le graphe n'est pas un clic).
    let depart: { x: number; y: number } | null = null;
    const appui = (e: PointerEvent) => {
      depart = { x: e.clientX, y: e.clientY };
    };
    const clic = (e: MouseEvent) => {
      const avant = a().selection;
      if (avant === null || !volet || volet.contains(e.target as Node)) return;
      const glisse = depart && Math.hypot(e.clientX - depart.x, e.clientY - depart.y) > 5;
      if (glisse) return;
      // Après les gestionnaires du clic : s'ils ont changé la sélection, on la laisse.
      setTimeout(() => {
        if (a().selection === avant) a().selectionner(null);
      }, 0);
    };
    window.addEventListener("keydown", echap);
    document.addEventListener("pointerdown", appui, true);
    document.addEventListener("click", clic, true);
    onCleanup(() => {
      window.removeEventListener("keydown", echap);
      document.removeEventListener("pointerdown", appui, true);
      document.removeEventListener("click", clic, true);
    });
  });

  return (
    <Show when={choix()}>
      {(c) => (
        <aside ref={(e) => (volet = e)} class="panneau" aria-label="Fiche projet">
          <button
            type="button"
            class="bouton-icone panneau-fermer"
            title="Fermer (Échap)"
            aria-label="Fermer la fiche"
            onClick={() => a().selectionner(null)}
          >
            ×
          </button>
          <FicheProjet
            portefeuille={a().portefeuille}
            projet={c().projet}
            voisins={a().similarites.voisins[c().indice] ?? []}
            similarites={a().similarites}
            aujourdhui={a().aujourdhui}
            surSelection={(ref) => a().selectionner(ref)}
          />
        </aside>
      )}
    </Show>
  );
}

/** L'équivalent sans couleur de ce que montre le graphe (ADR-0003 § 5 du POC). */
export function TableauRapprochements(props: { analyse: Analyse; liens: Arete[]; seuil: number }) {
  const projets = () => props.analyse.portefeuille.projets;
  // Pas de dessin : l'export n'offre que le tableau.
  declarerFigure({
    id: "rapprochements",
    tableau: () => ({
      colonnes: ["projet_a", "projet_b", "score"],
      lignes: props.liens.map((e) => [
        projets()[e.a]?.acronyme ?? "",
        projets()[e.b]?.acronyme ?? "",
        Number(e.score.toFixed(4)),
      ]),
    }),
  });
  const lien = (k: number) => (
    <button
      type="button"
      class="lien-projet"
      onClick={() => props.analyse.selectionner(projets()[k]?.ref ?? null)}
    >
      {projets()[k]?.acronyme}
    </button>
  );
  return (
    <section aria-labelledby="titre-rapprochements">
      <div class="entete-vue">
        <h2 id="titre-rapprochements">Rapprochements retenus</h2>
        <p class="explication discret">
          {pluriel(props.liens.length, "paire")} au seuil {formaterScore(props.seuil)}.
        </p>
      </div>
      <table class="tableau">
        <thead>
          <tr>
            <th scope="col">Projet</th>
            <th scope="col">Projet</th>
            <th scope="col" class="num">
              Score
            </th>
          </tr>
        </thead>
        <tbody>
          <For each={props.liens}>
            {(e) => (
              <tr>
                <td>{lien(e.a)}</td>
                <td>{lien(e.b)}</td>
                <td class="num">{formaterScore(e.score)}</td>
              </tr>
            )}
          </For>
        </tbody>
      </table>
    </section>
  );
}
